/**
 * MemoryConnector — in-process key-value store.
 * Not mountable. Used for scratch state, agent-to-agent coordination, config.
 */

import type {
  ConnectContext,
  ConnectorContent,
  ConnectorDeclaration,
  ConnectorEntry,
  ConnectorHandle,
  ListOptions,
  OperationDescriptor,
  SearchOptions,
  SearchResult,
  ToolFace,
} from "@skaile/workspaces/connectors";
import { AbstractConnector } from "@skaile/workspaces/connectors";

interface MemoryState {
  store: Map<string, string>;
  namespace: string;
}

/**
 * Connector for an in-process key-value store. Useful for scratch state, agent coordination, and config.
 * No external dependencies — the store lives entirely in process memory and is cleared on disconnect.
 * @docLink packages/factory-assets/concepts#memory-connector
 */
export class MemoryConnector extends AbstractConnector {
  readonly name = "memory";

  tools: ToolFace;

  constructor() {
    super();
    this.tools = {
      read: (handle, path) => this._read(handle, path),
      write: (handle, path, content) => this._write(handle, path, content),
      delete: (handle, path) => this._delete(handle, path),
      list: (handle, path, options) => this._list(handle, path, options),
      search: (handle, query, options) => this._search(handle, query, options),
      describeOperations: (handle) => this._describeOperations(handle),
      executeOp: (handle, operation, args) => this._executeOp(handle, operation, args),
    };
  }

  async connect(declaration: ConnectorDeclaration, ctx: ConnectContext): Promise<ConnectorHandle> {
    this.log.info("connect", { id: declaration.id });
    const namespace = String(declaration.options?.namespace ?? declaration.id);
    return this.makeHandle(declaration, { store: new Map(), namespace } satisfies MemoryState);
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("disconnect");
    (handle.state as MemoryState).store.clear();
    handle.status = "disconnected";
  }

  private store(handle: ConnectorHandle): Map<string, string> {
    return (handle.state as MemoryState).store;
  }

  private async _read(handle: ConnectorHandle, path: string): Promise<ConnectorContent | null> {
    const value = this.store(handle).get(path);
    if (value === undefined) return null;
    return { data: value, contentType: "application/json" };
  }

  private async _write(
    handle: ConnectorHandle,
    path: string,
    content: ConnectorContent,
  ): Promise<void> {
    const isNew = !this.store(handle).has(path);
    this.store(handle).set(
      path,
      typeof content.data === "string" ? content.data : content.data.toString("utf-8"),
    );
    this.emitChange?.({ path, action: isNew ? "create" : "edit", source: "operation" });
  }

  private async _delete(handle: ConnectorHandle, path: string): Promise<boolean> {
    const deleted = this.store(handle).delete(path);
    if (deleted) this.emitChange?.({ path, action: "delete", source: "operation" });
    return deleted;
  }

  private async _list(
    handle: ConnectorHandle,
    path?: string,
    options?: ListOptions,
  ): Promise<ConnectorEntry[]> {
    const prefix = path ?? "";
    const entries: ConnectorEntry[] = [];
    for (const key of this.store(handle).keys()) {
      if (prefix && !key.startsWith(prefix)) continue;
      if (options?.glob && !matchGlob(key, options.glob)) continue;
      if (options?.limit && entries.length >= options.limit) break;
      entries.push({ name: key, path: key, type: "key" });
    }
    return entries;
  }

  private async _search(
    handle: ConnectorHandle,
    query: string,
    options?: SearchOptions,
  ): Promise<SearchResult[]> {
    const results: SearchResult[] = [];
    for (const [key, value] of this.store(handle)) {
      if (options?.maxResults && results.length >= options.maxResults) break;
      if (key.includes(query) || value.includes(query)) {
        results.push({ path: key, snippet: value.slice(0, 200) });
      }
    }
    return results;
  }

  private _describeOperations(_handle: ConnectorHandle): OperationDescriptor[] {
    return [
      {
        name: "get",
        description: "Get value by key",
        accessLevel: "read",
        args: [{ name: "key", type: "string", required: true, description: "Key name" }],
        examples: ['get --key "config:theme"'],
      },
      {
        name: "set",
        description: "Set a key-value pair",
        accessLevel: "write",
        args: [
          { name: "key", type: "string", required: true, description: "Key name" },
          {
            name: "value",
            type: "string",
            required: true,
            description: "Value (stored as string)",
          },
        ],
        examples: ['set --key "status" --value "running"'],
      },
      {
        name: "keys",
        description: "List all keys matching a pattern",
        accessLevel: "read",
        args: [
          {
            name: "pattern",
            type: "string",
            required: false,
            description: "Glob pattern (default: all)",
          },
        ],
      },
      {
        name: "del",
        description: "Delete a key",
        accessLevel: "write",
        args: [{ name: "key", type: "string", required: true, description: "Key to delete" }],
      },
    ];
  }

  private async _executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string> {
    switch (operation) {
      case "get": {
        const key = String(args.key ?? "");
        const value = this.store(handle).get(key);
        return value ?? "";
      }
      case "set": {
        const key = String(args.key ?? "");
        const value = String(args.value ?? "");
        const isNew = !this.store(handle).has(key);
        this.store(handle).set(key, value);
        this.emitChange?.({ path: key, action: isNew ? "create" : "edit", source: "operation" });
        return "OK";
      }
      case "keys": {
        const pattern = args.pattern ? String(args.pattern) : undefined;
        const keys = [...this.store(handle).keys()];
        const filtered = pattern ? keys.filter((k) => matchGlob(k, pattern)) : keys;
        return JSON.stringify(filtered);
      }
      case "del": {
        const key = String(args.key ?? "");
        const deleted = this.store(handle).delete(key);
        if (deleted) this.emitChange?.({ path: key, action: "delete", source: "operation" });
        return deleted ? "Deleted" : "Not found";
      }
      default:
        throw new Error(`Memory adapter has no operation: ${operation}`);
    }
  }
}

function matchGlob(str: string, pattern: string): boolean {
  // Escape regex metacharacters first, then translate the glob wildcards so a
  // pattern like "a.b*" matches literally rather than as a regex.
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`^${escaped.replace(/\\\*/g, ".*").replace(/\\\?/g, ".")}$`);
  return regex.test(str);
}

/**
 * Creates a new MemoryConnector instance.
 * @returns Configured MemoryConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#memory-connector-factory
 */
export function createConnector(): MemoryConnector {
  return new MemoryConnector();
}
